import {
  getBlockchainAddressesForCurrency,
  MarketAccountOwnershipEnum,
  truncateBlockchainAddress,
  type MarketAccount,
} from '@talos/kyoko';
import { createElement, Fragment, type ReactNode } from 'react';
import { v4 as uuid } from 'uuid';

export const TRANSFER_RECENTS_GROUP = 'MOST RECENTLY USED';

export function getRecentTransferAccountsKey(provider: string, asset: string): string {
  return `${provider}::${asset}`;
}

export interface TransferForm {
  clientID: string;
  provider: string;
  amount: string;
  asset: string;
  transferAsset: string;
  chainCurrency: string | undefined;
  source: SourceDestinationOption | undefined;
  destination: SourceDestinationOption | undefined;
  reference: string;
  description: string;
  treatAsGrossAmount: boolean;
}

export function getEmptyTransferForm(): TransferForm {
  return {
    clientID: uuid(),
    provider: '',
    asset: '',
    transferAsset: '',
    chainCurrency: '',
    amount: '',
    source: undefined,
    destination: undefined,
    reference: '',
    description: '',
    treatAsGrossAmount: false,
  };
}

export interface SourceDestinationOption {
  label: string;
  description?: string;
  addressForCurrency?: string;
  marketAccount: MarketAccount;
}

type TransferAccount = SourceDestinationOption | string | undefined;

export const SAME_ACCOUNT_TRANSFER_ERROR = 'Destination must be different from source';
export const MISMATCHED_CREDENTIAL_TRANSFER_ERROR = 'Source and Destination accounts must use same API Credential';

function getTransferAccountCredentialID(
  account: TransferAccount,
  marketAccountsByName?: ReadonlyMap<string, MarketAccount>
): number | undefined {
  if (account == null) {
    return undefined;
  }
  if (typeof account === 'string') {
    return marketAccountsByName?.get(account)?.CredentialID;
  }
  return account.marketAccount.CredentialID;
}

export function areSameTransferAccounts(source: TransferAccount, destination: TransferAccount): boolean {
  if (source == null || destination == null) {
    return false;
  }

  const sourceName = typeof source === 'string' ? source : source.marketAccount.Name;
  const destinationName = typeof destination === 'string' ? destination : destination.marketAccount.Name;

  return sourceName === destinationName;
}

export function haveMismatchedTransferCredentials(
  source: TransferAccount,
  destination: TransferAccount,
  marketAccountsByName?: ReadonlyMap<string, MarketAccount>
): boolean {
  const sourceCredentialID = getTransferAccountCredentialID(source, marketAccountsByName);
  const destinationCredentialID = getTransferAccountCredentialID(destination, marketAccountsByName);

  return (
    sourceCredentialID != null && destinationCredentialID != null && sourceCredentialID !== destinationCredentialID
  );
}

interface GetTransferAccountsCompatibilityErrorParams {
  source: TransferAccount;
  destination: TransferAccount;
  marketAccountsByName?: ReadonlyMap<string, MarketAccount>;
}

export function getSameAccountTransferError({
  source,
  destination,
}: Pick<GetTransferAccountsCompatibilityErrorParams, 'source' | 'destination'>): string | undefined {
  if (areSameTransferAccounts(source, destination)) {
    return SAME_ACCOUNT_TRANSFER_ERROR;
  }

  return undefined;
}

export function getMismatchedCredentialTransferError({
  source,
  destination,
  marketAccountsByName,
}: GetTransferAccountsCompatibilityErrorParams): string | undefined {
  if (haveMismatchedTransferCredentials(source, destination, marketAccountsByName)) {
    return MISMATCHED_CREDENTIAL_TRANSFER_ERROR;
  }

  return undefined;
}

export function getTransferAccountsCompatibilityError({
  source,
  destination,
  marketAccountsByName,
}: GetTransferAccountsCompatibilityErrorParams): string | undefined {
  return (
    getSameAccountTransferError({ source, destination }) ??
    getMismatchedCredentialTransferError({ source, destination, marketAccountsByName })
  );
}

/** FormGroup hides help when error is set; merge both into error so balance help stays visible. */
export function errorWithHelp(
  error: ReactNode | undefined,
  help: ReactNode | undefined
): { error?: ReactNode; help?: ReactNode } {
  if (error != null && help != null) {
    return {
      error: createElement(Fragment, null, help, ' • ', error),
    };
  }

  return {
    error: error ?? undefined,
    help: help ?? undefined,
  };
}

export function shouldShowDestinationBalance(marketAccount: MarketAccount): boolean {
  return marketAccount.Ownership === MarketAccountOwnershipEnum.Internal;
}

export function marketAccountToSourceDestinationOption(
  marketAccount: MarketAccount,
  currency: string | undefined
): SourceDestinationOption {
  const blockchainAddressForCurrency = currency
    ? getBlockchainAddressesForCurrency(marketAccount, currency)?.at(0)?.Address
    : undefined;

  return {
    label: marketAccount.DisplayName || marketAccount.Name,
    description: blockchainAddressForCurrency ? truncateBlockchainAddress(blockchainAddressForCurrency) : undefined,
    addressForCurrency: blockchainAddressForCurrency,
    marketAccount,
  };
}
